import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { RetryableError } from '../../../shared/RetryableError';
import { RestrictionLockedDialog } from '../../../shared/LockedFeatureDialog';
import { upgradeRequiredFromError, type SubscriptionRestriction } from '../../../shared/subscriptionRestriction';
import { ApiError } from '../../identity/types';
import { jobRoleGateway } from '../index';
import { isForbidden, jobRoleErrorMessage } from '../jobRoleErrors';
import { basisUnit, formatHours, formatMoney } from '../payBand';
import {
  MAX_ANNUAL_HOURS, SYNTHETIC_DATA_EXPLANATION, SYNTHETIC_DATA_LABEL,
  type BenchmarkGeographyType, type BenchmarkQuery, type BenchmarkResult, type BenchmarkSourceRegistryItem, type BenchmarkSourceResult, type PayBasis,
} from '../types';
import { US_STATES } from '../usStates';
import { FieldGuidance } from '../guidance/FieldGuidance';
import { fieldDef } from '../guidance/roleFieldRegistry';
import { ErrorSummary } from './ErrorSummary';
import { formatDateTime, useTabLoad } from './tabLoad';

/** The badge shown wherever a Synthetic source's numbers appear. The words carry the meaning; the colour only helps. */
export function SyntheticBadge() {
  return <span className="jr-badge jr-badge--synthetic" data-testid="synthetic-badge">{SYNTHETIC_DATA_LABEL}</span>;
}

export const NO_PERMISSION_TEXT =
  'You do not have permission to view market benchmarks. Viewing them needs the role compensation benchmarks permission, which is not part of every access role. Ask a company administrator if you need it.';

const dateOnly = (iso: string | null): string => {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const ordinal = (n: number): string => `${n}th`;

function SourceResult({ source, annualHours }: { source: BenchmarkSourceResult; annualHours: number }) {
  const uid = useId();
  const synthetic = source.kind === 'Synthetic';
  const converted = source.originalBasis !== source.convertedBasis;
  const requested = source.requestedGeography;
  const resolved = source.resolvedGeography;
  return (
    <section className="jr-source" aria-labelledby={`${uid}-title`} data-testid="benchmark-source">
      <h5 id={`${uid}-title`} className="jr-source__title">
        {source.title} {synthetic && <SyntheticBadge />}
      </h5>
      <p className="muted jr-source__org">{source.organization}</p>
      {synthetic && <p className="jr-warning" role="note"><span aria-hidden="true">{'⚠'} </span>{SYNTHETIC_DATA_LABEL}. {SYNTHETIC_DATA_EXPLANATION}</p>}

      <p className="jr-source__geo" data-testid="benchmark-geography">
        <strong>Geography:</strong>{' '}
        {source.fallback.applied
          ? <>Showing {resolved.name} ({resolved.type}); you asked for {requested.name || requested.type} ({requested.type}).{' '}
              {source.fallback.reason && <span data-testid="fallback-reason">{source.fallback.reason}</span>}</>
          : <>{resolved.name} ({resolved.type})</>}
      </p>

      <div className="jr-table-scroll" role="region" aria-label={`Benchmark values from ${source.title}`} tabIndex={0}>
        <table className="jr-table">
          <caption className="jr-sr-only">Market benchmark values from {source.title}{synthetic ? ` (${SYNTHETIC_DATA_LABEL})` : ''}</caption>
          <thead>
            <tr>
              <th scope="col">Market point</th>
              <th scope="col">Percentile reported</th>
              <th scope="col">Value as the source reports it</th>
              <th scope="col">Value shown ({source.convertedBasis === 'Hourly' ? 'per hour' : 'per year'})</th>
            </tr>
          </thead>
          <tbody>
            {source.points.map((p) => (
              <tr key={`${p.label}-${p.percentile}`}>
                <th scope="row">{p.label} ({ordinal(p.percentile)} percentile)</th>
                <td>{ordinal(p.percentile)}</td>
                <td>{formatMoney(p.originalValue, source.originalBasis)} {basisUnit(source.originalBasis)}</td>
                <td>{formatMoney(p.value, source.convertedBasis)} {basisUnit(source.convertedBasis)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {converted
        ? <p className="jr-hint">Converted from {source.originalBasis.toLowerCase()} to {source.convertedBasis.toLowerCase()} using {formatHours(annualHours)} annual hours. No other adjustment is made.</p>
        : <p className="jr-hint">Shown as the source reports it. No conversion was needed.</p>}
      {source.percentileDefinition && <p className="jr-hint">{source.percentileDefinition}</p>}

      <dl className="jr-facts jr-facts--tight">
        <div className="jr-facts__row"><dt>Dataset edition</dt><dd>{source.datasetEdition}</dd></div>
        <div className="jr-facts__row"><dt>Reference year</dt><dd>{source.referenceYear}</dd></div>
        <div className="jr-facts__row"><dt>Retrieved</dt><dd>{dateOnly(source.retrievalDate)}</dd></div>
        <div className="jr-facts__row"><dt>Last updated</dt><dd>{formatDateTime(source.lastUpdatedUtc)}</dd></div>
        {source.expirationDate && <div className="jr-facts__row"><dt>Expires</dt><dd>{dateOnly(source.expirationDate)}</dd></div>}
        <div className="jr-facts__row">
          <dt>Data availability</dt>
          <dd>{source.dataAvailability === 'Full' ? 'Full: low, median and high are all reported' : 'Partial: only some of low, median and high are reported'}</dd>
        </div>
        {source.sampleSize !== null && <div className="jr-facts__row"><dt>Sample size</dt><dd>{source.sampleSize.toLocaleString('en-US')}</dd></div>}
        {source.reliabilityNote && <div className="jr-facts__row"><dt>Reliability note</dt><dd>{source.reliabilityNote}</dd></div>}
      </dl>
      {source.attributionText && <p className="jr-hint" data-testid="attribution">{source.attributionText}</p>}
      {source.url && (
        <p className="jr-hint">
          <a href={source.url} target="_blank" rel="noopener noreferrer">Source website<span className="jr-sr-only"> for {source.title} (opens in a new tab)</span></a>
        </p>
      )}
    </section>
  );
}

/** The source registry: which sources exist and whether each is enabled. Loaded when asked for, and only for someone allowed to see benchmarks. */
function SourceRegistry() {
  const [open, setOpen] = useState(false);
  return (
    <div className="jr-registry">
      <button type="button" className="link" aria-expanded={open} onClick={() => setOpen((v) => !v)}>{open ? 'Hide benchmark sources' : 'Show benchmark sources'}</button>
      {open && <RegistryTable />}
    </div>
  );
}

function RegistryTable() {
  const { state, retry } = useTabLoad<BenchmarkSourceRegistryItem[]>((signal) => jobRoleGateway.listBenchmarkSources(signal), [], 'Could not load the benchmark sources.');
  if (state.kind === 'loading') return <p className="muted" role="status">Loading benchmark sources{'…'}</p>;
  if (state.kind === 'forbidden') return <p className="jr-notice" role="note">{NO_PERMISSION_TEXT}</p>;
  if (state.kind === 'error') return <RetryableError message={state.message} onRetry={retry} />;
  return (
    <div className="jr-table-scroll" role="region" aria-label="Benchmark sources table" tabIndex={0}>
      <table className="jr-table">
        <caption className="jr-sr-only">Benchmark sources and whether each one is enabled</caption>
        <thead><tr><th scope="col">Source</th><th scope="col">Kind</th><th scope="col">License review</th><th scope="col">Enabled</th><th scope="col">Latest import</th></tr></thead>
        <tbody>
          {state.data.map((s) => (
            <tr key={s.code}>
              <th scope="row">{s.title} <span className="muted">({s.code})</span></th>
              <td>{s.kind === 'Synthetic' ? <>Synthetic <SyntheticBadge /></> : s.kind.replace(/([a-z])([A-Z])/g, '$1 $2')}</td>
              <td>{s.licenseStatus === 'PendingReview' ? 'Pending review' : s.licenseStatus.replace(/([a-z])([A-Z])/g, '$1 $2')}</td>
              <td>{s.isEnabled ? 'Enabled' : 'Not enabled'}</td>
              <td>{s.latestBatch ? `${s.latestBatch.datasetEdition} (${s.latestBatch.referenceYear})` : 'Nothing imported'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface Controls {
  geographyType: BenchmarkGeographyType;
  state: string;
  metroCode: string;
  referenceYear: string;
  basis: PayBasis;
  annualHours: string;
}

const INITIAL_CONTROLS: Controls = { geographyType: 'National', state: '', metroCode: '', referenceYear: '', basis: 'Annual', annualHours: '' };

/** The controls as a request. Empty fields are left out so the server's defaults (latest year, default annual hours) apply. */
function queryFrom(c: Controls): BenchmarkQuery {
  return {
    geographyType: c.geographyType,
    basis: c.basis,
    ...(c.geographyType !== 'National' && c.state ? { state: c.state } : {}),
    ...(c.geographyType === 'Metro' && c.metroCode.trim() ? { metroCode: c.metroCode.trim() } : {}),
    ...(c.referenceYear.trim() ? { referenceYear: Number(c.referenceYear) } : {}),
    ...(c.annualHours.trim() ? { annualHours: Number(c.annualHours) } : {}),
  };
}

function controlIssues(c: Controls): { key: string; message: string }[] {
  const issues: { key: string; message: string }[] = [];
  if (c.geographyType === 'State' && !c.state) issues.push({ key: 'state', message: 'Choose a state, or switch the geography to National.' });
  if (c.geographyType === 'Metro' && !c.metroCode.trim()) issues.push({ key: 'metroCode', message: 'Enter a metro area code, or switch the geography.' });
  if (c.referenceYear.trim() && !(/^\d{4}$/.test(c.referenceYear.trim()))) issues.push({ key: 'referenceYear', message: 'Enter the reference year as four digits, like 2025, or leave it empty for the latest.' });
  if (c.annualHours.trim()) {
    const hours = Number(c.annualHours);
    if (!Number.isFinite(hours) || hours < 1 || hours > MAX_ANNUAL_HOURS) issues.push({ key: 'annualHours', message: `Enter the annual hours as a number from 1 to ${MAX_ANNUAL_HOURS.toLocaleString('en-US')}, or leave it empty for the default.` });
  }
  return issues;
}

type Phase =
  | { kind: 'loading' } | { kind: 'ready'; result: BenchmarkResult } | { kind: 'forbidden' }
  /** The plan does not include market benchmarks (403 SUBSCRIPTION_UPGRADE_REQUIRED): a calm note, not an error and not a permission problem. */
  | { kind: 'plan'; restriction: SubscriptionRestriction }
  | { kind: 'error'; message: string };

export interface BenchmarkPanelProps {
  roleId: string;
  /** Bumped by a parent that changed the role (for example its SOC code) so the lookup runs again. */
  refreshKey?: number;
  /** The server's capability flag `canViewBenchmarks`: when it is false the calm "no permission" state is shown WITHOUT asking the server. Absent when
   * the flag is unknown, then the lookup runs and a 403 is handled as before. */
  allowed?: boolean | undefined;
}

/**
 * Market benchmark view for one role: controls (geography, state, reference year, hourly or annual view, annual hours), then each
 * source's own table (never blended), the geography and fallback explanation, the limits of the data, and "EPN suggested range:
 * Unavailable" with its reason. A 403 is a calm "no permission" state, `NoSocCode` and `NoData` show no number, and an error is retryable.
 * No employee's pay is read or shown. The server is the security boundary: this only reacts to what it answers.
 */
export function BenchmarkPanel({ roleId, refreshKey = 0, allowed }: BenchmarkPanelProps) {
  if (allowed === false) {
    return <p className="jr-notice" role="note" data-testid="benchmark-forbidden"><span aria-hidden="true">{'🔒'} </span>{NO_PERMISSION_TEXT}</p>;
  }
  return <BenchmarkLookup roleId={roleId} refreshKey={refreshKey} />;
}

function BenchmarkLookup({ roleId, refreshKey = 0 }: { roleId: string; refreshKey?: number }) {
  const uid = useId();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [controls, setControls] = useState<Controls>(INITIAL_CONTROLS);
  const [applied, setApplied] = useState<BenchmarkQuery>({});
  const [nonce, setNonce] = useState(0);
  const [issues, setIssues] = useState<{ key: string; message: string }[]>([]);
  const [applyError, setApplyError] = useState<string | null>(null);
  const hasResult = useRef(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [explaining, setExplaining] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPhase((prev) => (prev.kind === 'ready' ? prev : { kind: 'loading' }));
    jobRoleGateway
      // `quiet`: the panel explains a plan refusal itself, so no app-wide notice is raised for it.
      .getBenchmarks(roleId, applied, controller.signal, { quiet: true })
      .then((result) => {
        if (controller.signal.aborted) return;
        hasResult.current = true;
        setLoadedOnce(true);
        setApplyError(null);
        setPhase({ kind: 'ready', result });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const planRefusal = upgradeRequiredFromError(err);
        if (planRefusal) {
          setPhase({ kind: 'plan', restriction: planRefusal });
        } else if (isForbidden(err)) {
          setPhase({ kind: 'forbidden' });
        } else if (err instanceof ApiError && err.status === 400 && hasResult.current) {
          // A request the server refused (for example a bad year): keep the numbers on screen and say what to change.
          setApplyError(jobRoleErrorMessage(err, 'Could not update the benchmarks.'));
        } else {
          setPhase({ kind: 'error', message: jobRoleErrorMessage(err, 'Could not load market benchmarks.') });
        }
      });
    return () => controller.abort();
  }, [roleId, applied, nonce, refreshKey]);

  useEffect(() => {
    if (issues.length > 0) summaryRef.current?.focus();
  }, [issues]);

  const retry = useCallback(() => { setPhase({ kind: 'loading' }); setNonce((n) => n + 1); }, []);

  function onApply(e: FormEvent) {
    e.preventDefault();
    const found = controlIssues(controls);
    setIssues(found);
    if (found.length > 0) return;
    setApplyError(null);
    setPhase((prev) => (prev.kind === 'ready' ? prev : { kind: 'loading' }));
    setApplied(queryFrom(controls));
    setNonce((n) => n + 1);
  }

  const set = <K extends keyof Controls>(key: K, value: Controls[K]) => {
    setControls((prev) => ({ ...prev, [key]: value }));
    setIssues((prev) => prev.filter((i) => i.key !== key));
  };
  const issueOf = (key: string) => issues.find((i) => i.key === key)?.message;
  const focusField = (key: string) => rootRef.current?.querySelector<HTMLElement>(`[data-field="${key}"]`)?.focus();

  if (phase.kind === 'loading' && !loadedOnce) return <p className="muted" role="status">Loading market benchmarks{'…'}</p>;
  if (phase.kind === 'forbidden') return <p className="jr-notice" role="note" data-testid="benchmark-forbidden"><span aria-hidden="true">{'🔒'} </span>{NO_PERMISSION_TEXT}</p>;
  if (phase.kind === 'plan') {
    const planName = phase.restriction.requiredPlan ?? 'a higher plan';
    return (
      <div className="jr-notice" role="note" data-testid="benchmark-plan-locked">
        <p><span aria-hidden="true">{'🔒'} </span>Market pay benchmarks are included in {planName}. Your pay band and everything else on this role work as usual.</p>
        <p>
          <button type="button" className="link" aria-haspopup="dialog" onClick={() => setExplaining(true)}>What changes?</button>
        </p>
        {explaining && (
          <RestrictionLockedDialog restriction={phase.restriction} fallbackFeatureName="Market pay benchmarks" onClose={() => setExplaining(false)} />
        )}
      </div>
    );
  }
  if (phase.kind === 'error') return <div data-testid="benchmark-error"><RetryableError message={phase.message} onRetry={retry} /></div>;

  const result = phase.kind === 'ready' ? phase.result : null;
  const showControls = result === null || result.status !== 'NoSocCode';
  const needsState = controls.geographyType !== 'National';

  return (
    <div ref={rootRef} className="jr-benchmark">
      {showControls && (
        <form className="jr-bench-controls" onSubmit={onApply} noValidate aria-label="Benchmark view">
          <fieldset className="jr-bench-fieldset">
            <legend>Benchmark view</legend>
            <ErrorSummary ref={summaryRef} issues={issues} onSelect={focusField} />
            <div className="jr-bench-grid">
              <div className="jr-field">
                <div className="jr-fieldhead"><label htmlFor={`${uid}-geo`}>Geography</label><FieldGuidance field={fieldDef('benchmarkGeography')} /></div>
                <select id={`${uid}-geo`} data-field="geographyType" value={controls.geographyType} onChange={(e) => set('geographyType', e.target.value as BenchmarkGeographyType)}>
                  <option value="National">National</option>
                  <option value="State">State</option>
                  <option value="Metro">Metro area</option>
                </select>
              </div>
              {needsState && (
                <div className="jr-field">
                  <label htmlFor={`${uid}-state`}>{controls.geographyType === 'Metro' ? 'State of the metro area (optional)' : 'State'}</label>
                  <select
                    id={`${uid}-state`} data-field="state" value={controls.state} onChange={(e) => set('state', e.target.value)}
                    aria-invalid={issueOf('state') ? true : undefined} aria-describedby={issueOf('state') ? `${uid}-state-err` : undefined}
                  >
                    <option value="">{controls.geographyType === 'Metro' ? 'Not specified' : 'Choose a state'}</option>
                    {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                  </select>
                  {issueOf('state') && <p id={`${uid}-state-err`} className="jr-error">{issueOf('state')}</p>}
                </div>
              )}
              {controls.geographyType === 'Metro' && (
                <div className="jr-field">
                  <label htmlFor={`${uid}-metro`}>Metro area code</label>
                  <input
                    id={`${uid}-metro`} data-field="metroCode" type="text" inputMode="numeric" autoComplete="off" value={controls.metroCode} onChange={(e) => set('metroCode', e.target.value)}
                    aria-invalid={issueOf('metroCode') ? true : undefined} aria-describedby={`${uid}-metro-hint${issueOf('metroCode') ? ` ${uid}-metro-err` : ''}`}
                  />
                  <p id={`${uid}-metro-hint`} className="jr-hint">The metropolitan area code the source uses.</p>
                  {issueOf('metroCode') && <p id={`${uid}-metro-err`} className="jr-error">{issueOf('metroCode')}</p>}
                </div>
              )}
              <div className="jr-field">
                <div className="jr-fieldhead"><label htmlFor={`${uid}-year`}>Reference year</label><FieldGuidance field={fieldDef('benchmarkReferenceYear')} /></div>
                <input
                  id={`${uid}-year`} data-field="referenceYear" type="text" inputMode="numeric" autoComplete="off" placeholder="Latest available" value={controls.referenceYear}
                  onChange={(e) => set('referenceYear', e.target.value)} aria-invalid={issueOf('referenceYear') ? true : undefined}
                  aria-describedby={issueOf('referenceYear') ? `${uid}-year-err` : undefined}
                />
                {issueOf('referenceYear') && <p id={`${uid}-year-err`} className="jr-error">{issueOf('referenceYear')}</p>}
              </div>
              <div className="jr-field">
                <div className="jr-fieldhead"><label htmlFor={`${uid}-basis`}>Show values as</label><FieldGuidance field={fieldDef('benchmarkBasis')} /></div>
                <select id={`${uid}-basis`} data-field="basis" value={controls.basis} onChange={(e) => set('basis', e.target.value as PayBasis)}>
                  <option value="Annual">Annual</option>
                  <option value="Hourly">Hourly</option>
                </select>
              </div>
              <div className="jr-field">
                <div className="jr-fieldhead"><label htmlFor={`${uid}-hours`}>Annual hours</label><FieldGuidance field={fieldDef('benchmarkAnnualHours')} /></div>
                <input
                  id={`${uid}-hours`} data-field="annualHours" type="text" inputMode="decimal" autoComplete="off"
                  placeholder={result ? String(result.request.annualHours) : 'Default'} value={controls.annualHours} onChange={(e) => set('annualHours', e.target.value)}
                  aria-invalid={issueOf('annualHours') ? true : undefined} aria-describedby={`${uid}-hours-hint${issueOf('annualHours') ? ` ${uid}-hours-err` : ''}`}
                />
                <p id={`${uid}-hours-hint`} className="jr-hint">Used only to convert between hourly and annual values.</p>
                {issueOf('annualHours') && <p id={`${uid}-hours-err`} className="jr-error">{issueOf('annualHours')}</p>}
              </div>
            </div>
            <div className="jr-actions">
              <button type="submit" className="jr-btn jr-btn--secondary">Update benchmarks</button>
            </div>
            {applyError && <p className="jr-error jr-error--block" role="alert">{applyError}</p>}
          </fieldset>
        </form>
      )}

      {phase.kind === 'loading' && <p className="muted" role="status">Updating market benchmarks{'…'}</p>}

      {result && result.status === 'NoSocCode' && (
        <div className="jr-notice" role="note" data-testid="benchmark-nosoc">
          <p><strong>No SOC code on this role.</strong></p>
          <p>{result.message ?? 'Market benchmarks are looked up by SOC code, and this role does not have one. Add a SOC code in the role details to see benchmarks.'}</p>
          <p className="muted">No benchmark figure is shown.</p>
        </div>
      )}

      {result && result.status === 'NoData' && (
        <div className="jr-notice" role="note" data-testid="benchmark-nodata">
          <p><strong>No benchmark data to show.</strong></p>
          <p>{result.message ?? 'No benchmark data has been imported for this occupation. Benchmarks stay unavailable until a licensed or approved dataset is imported.'}</p>
          <p className="muted">No benchmark figure is shown, and nothing is estimated in its place.</p>
          {result.excluded.length > 0 && (
            <ul className="jr-plain-list">{result.excluded.map((e) => <li key={`${e.sourceCode}-${e.reason}`}>{e.sourceCode}: {e.reason}</li>)}</ul>
          )}
          <SourceRegistry />
        </div>
      )}

      {result && result.status === 'Available' && (
        <>
          <p className="jr-hint" data-testid="benchmark-request">
            Showing {result.request.basis === 'Hourly' ? 'hourly' : 'annual'} values for {result.socCode ? `SOC ${result.socCode}` : 'this role'}, reference year {result.request.referenceYear ?? 'latest'}.
            Annual hours used for conversion: {formatHours(result.request.annualHours)} ({result.request.annualHoursSource === 'default' ? 'the default' : 'your choice'}).
          </p>
          <p className="jr-hint">Sources are shown side by side and never combined into one figure.</p>
          {result.sources.map((s) => <SourceResult key={`${s.sourceCode}-${s.referenceYear}`} source={s} annualHours={result.request.annualHours} />)}
          {result.excluded.length > 0 && (
            <div className="jr-notice" role="note">
              <p><strong>Not shown</strong></p>
              <ul className="jr-plain-list">{result.excluded.map((e) => <li key={`${e.sourceCode}-${e.reason}`}>{e.sourceCode}: {e.reason}</li>)}</ul>
            </div>
          )}
          <p className="jr-suggested" data-testid="suggested-range">
            <strong>EPN suggested range:</strong> Unavailable. {result.suggestedRange.reason ?? 'No aggregation methodology has been approved. Sources are shown separately.'}
          </p>
          {result.limitations.length > 0 && (
            <div className="jr-block">
              <h5>Limits of this data</h5>
              <ul>{result.limitations.map((l) => <li key={l}>{l}</li>)}</ul>
            </div>
          )}
          {result.disclaimer && <p className="jr-hint">{result.disclaimer}</p>}
          <SourceRegistry />
        </>
      )}
    </div>
  );
}
